import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { registerUiFeedbackHost } from "../utils/uiFeedback";
import "../styles/UiFeedback.css";

const TEXT = {
  ru: ["Подтвердите действие", "Введите данные", "Подтвердить", "Отмена", "Закрыть"],
  uk: ["Підтвердьте дію", "Введіть дані", "Підтвердити", "Скасувати", "Закрити"],
  en: ["Confirm action", "Enter details", "Confirm", "Cancel", "Close"],
  de: ["Aktion bestätigen", "Daten eingeben", "Bestätigen", "Abbrechen", "Schließen"],
  fr: ["Confirmer l’action", "Saisir les données", "Confirmer", "Annuler", "Fermer"],
  es: ["Confirmar acción", "Introducir datos", "Confirmar", "Cancelar", "Cerrar"],
  it: ["Conferma azione", "Inserisci i dati", "Conferma", "Annulla", "Chiudi"],
  pl: ["Potwierdź działanie", "Wprowadź dane", "Potwierdź", "Anuluj", "Zamknij"],
};

const getText = () => {
  const raw =
    document.documentElement.lang ||
    window.localStorage.getItem("i18nextLng") ||
    navigator.language ||
    "ru";
  const key = String(raw).toLowerCase().split("-")[0];
  const [confirmTitle, promptTitle, confirm, cancel, close] =
    TEXT[key] || TEXT.en;
  return { confirmTitle, promptTitle, confirm, cancel, close };
};

const ICONS = { success: "✓", error: "!", warning: "!", info: "i" };
const DURATIONS = { success: 3200, error: 5200, warning: 4600, info: 3800 };

const UiFeedbackHost = () => {
  const [notices, setNotices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const timersRef = useRef(new Map());
  const previousFocusRef = useRef(null);
  const primaryRef = useRef(null);
  const inputRef = useRef(null);

  const current = requests[0] || null;
  const text = useMemo(getText, [current?.id]);

  const dismissNotice = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) window.clearTimeout(timer);
    timersRef.current.delete(id);
    setNotices((items) => items.filter((item) => item.id !== id));
  }, []);

  const receiveNotice = useCallback((notice) => {
    setNotices((items) => [
      ...items.filter((item) => item.id !== notice.id),
      notice,
    ].slice(-4));

    const duration = notice.duration ?? DURATIONS[notice.tone] ?? DURATIONS.info;
    if (duration > 0) {
      const timer = window.setTimeout(() => dismissNotice(notice.id), duration);
      timersRef.current.set(notice.id, timer);
    }
  }, [dismissNotice]);

  const receiveRequest = useCallback((request) => {
    setRequests((items) => [...items, request]);
  }, []);

  useEffect(() => registerUiFeedbackHost({
    notify: receiveNotice,
    request: receiveRequest,
  }), [receiveNotice, receiveRequest]);

  useEffect(() => () => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!current) return undefined;

    previousFocusRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setInputValue(current.defaultValue || "");

    const timer = window.setTimeout(() => {
      if (current.kind === "prompt") {
        inputRef.current?.focus();
        inputRef.current?.select();
      } else {
        primaryRef.current?.focus();
      }
    }, 20);

    return () => {
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
      previousFocusRef.current?.focus?.();
    };
  }, [current?.id, current?.kind, current?.defaultValue]);

  const finish = useCallback((result) => {
    if (!current) return;
    current.resolve(result);
    setRequests((items) => items.slice(1));
  }, [current]);

  useEffect(() => {
    if (!current) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish(current.kind === "prompt" ? null : false);
      }

      if (event.key === "Enter" && current.kind === "confirm") {
        event.preventDefault();
        finish(true);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [current, finish]);

  const submitPrompt = (event) => {
    event.preventDefault();
    if (current?.required && !inputValue.trim()) {
      inputRef.current?.focus();
      return;
    }
    finish(inputValue.trim());
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      <div className="mp-feedback-stack" aria-live="polite">
        {notices.map((notice) => (
          <article
            key={notice.id}
            className={`mp-feedback-toast mp-feedback-toast--${notice.tone}`}
            role={notice.tone === "error" ? "alert" : "status"}
          >
            <span className="mp-feedback-toast__icon" aria-hidden="true">
              {ICONS[notice.tone] || ICONS.info}
            </span>
            <div className="mp-feedback-toast__content">
              {notice.title && (
                <strong className="mp-feedback-toast__title">
                  {notice.title}
                </strong>
              )}
              <span className="mp-feedback-toast__message">
                {notice.message}
              </span>
            </div>
            <button
              type="button"
              className="mp-feedback-toast__close"
              onClick={() => dismissNotice(notice.id)}
              aria-label={text.close}
            >
              ×
            </button>
          </article>
        ))}
      </div>

      {current && (
        <div
          className="mp-feedback-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              finish(current.kind === "prompt" ? null : false);
            }
          }}
        >
          <section
            className={`mp-feedback-dialog mp-feedback-dialog--${current.variant}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="mp-feedback-title"
            aria-describedby="mp-feedback-message"
          >
            <div className="mp-feedback-dialog__mark" aria-hidden="true">
              {current.variant === "danger" ? "!" : "✓"}
            </div>

            <div className="mp-feedback-dialog__body">
              <h2 id="mp-feedback-title" className="mp-feedback-dialog__title">
                {current.title ||
                  (current.kind === "prompt"
                    ? text.promptTitle
                    : text.confirmTitle)}
              </h2>

              {current.message && (
                <p id="mp-feedback-message" className="mp-feedback-dialog__message">
                  {current.message}
                </p>
              )}

              {current.kind === "prompt" ? (
                <form className="mp-feedback-dialog__form" onSubmit={submitPrompt}>
                  <input
                    ref={inputRef}
                    className="mp-feedback-dialog__input"
                    type={current.inputType}
                    value={inputValue}
                    placeholder={current.placeholder}
                    required={current.required}
                    onChange={(event) => setInputValue(event.target.value)}
                  />
                  <div className="mp-feedback-dialog__actions">
                    <button
                      type="button"
                      className="mp-feedback-dialog__button mp-feedback-dialog__button--secondary"
                      onClick={() => finish(null)}
                    >
                      {current.cancelLabel || text.cancel}
                    </button>
                    <button
                      ref={primaryRef}
                      type="submit"
                      className="mp-feedback-dialog__button mp-feedback-dialog__button--primary"
                    >
                      {current.confirmLabel || text.confirm}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mp-feedback-dialog__actions">
                  <button
                    type="button"
                    className="mp-feedback-dialog__button mp-feedback-dialog__button--secondary"
                    onClick={() => finish(false)}
                  >
                    {current.cancelLabel || text.cancel}
                  </button>
                  <button
                    ref={primaryRef}
                    type="button"
                    className={`mp-feedback-dialog__button ${
                      current.variant === "danger"
                        ? "mp-feedback-dialog__button--danger"
                        : "mp-feedback-dialog__button--primary"
                    }`}
                    onClick={() => finish(true)}
                  >
                    {current.confirmLabel || text.confirm}
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </>,
    document.body
  );
};

export default UiFeedbackHost;
